import { createContext, useContext, useMemo } from "react";

import { trpc } from "@blis/trpc/react";

import { useAuth } from "@/features/auth/hooks";
import type { TPanel, TTestSpec } from "@/lib/types";

type FormInfoContextProps = {
  panels: TPanel[];
  tests: TTestSpec[];
  types: string[];
  orgs: { id: number; name: string }[];
  loading: boolean;
};

const FormInfoContext = createContext<FormInfoContextProps>(
  {} as FormInfoContextProps,
);

export function FormInfoProvider({ children }: { children: React.ReactNode }) {
  const panelQuery = trpc.panels.all.useQuery();
  const testQuery = trpc.tests.allSelectable.useQuery();
  const orgsQuery = trpc.orgs.list.useQuery();

  const loading = panelQuery.isPending || testQuery.isPending;

  const panels = useMemo(() => {
    return panelQuery.data || [];
  }, [panelQuery.data]);

  const tests = useMemo(() => {
    return testQuery.data || [];
  }, [testQuery.data]);

  const orgs = useMemo(() => {
    return orgsQuery.data || [];
  }, [orgsQuery.data]);

  const types = useMemo(() => {
    const testTypes = tests.map((t) => t.type);
    const panelTypes = panels.flatMap((p) => p.tests.map((t) => t.type));
    return [...new Set(testTypes.concat(panelTypes))];
  }, [tests, panels]);

  return (
    <FormInfoContext.Provider
      value={{
        orgs,
        panels,
        tests,
        types,
        loading,
      }}
    >
      {children}
    </FormInfoContext.Provider>
  );
}

export function useFormInfo() {
  const context = useContext(FormInfoContext);
  if (!context.panels) {
    throw new Error("`useFormInfo` must be called within `FormInfoProvider`");
  }
  return context;
}

export function useFormPrimaryOrg() {
  const { user } = useAuth();

  const isLabRole = user?.hasRole([
    "blis_lab_manager",
    "blis_lab_tech",
    "blis_lab_readonly",
    "blis_lab_assistant",
  ]);
  const org: { id: number; name: string } | undefined = isLabRole
    ? undefined
    : user?.profile.org;
  const allowedOrgs: "all" | number[] = isLabRole
    ? "all"
    : ([org?.id, ...(user?.profile.allowedOrgs || [])].filter(
        Boolean,
      ) as number[]);
  const isDisabled =
    !isLabRole && (user?.profile.allowedOrgs || []).length === 0;

  return { org, allowedOrgs, isDisabled };
}

export function useFormReceivingOrg() {
  const { user } = useAuth();
  const { data } = trpc.orgs.primaryLabs.useQuery();

  if (user?.isLabRole()) {
    return data?.find((lab) => lab.id === user.profile.org.id)?.id;
  }

  return data?.find((lab) => lab.isUserPrimaryLab)?.id;
}
